import { cvAPI, cvEmployersAPI, cvRolesAPI } from 'api/CvAPI'
import { IItemDraftHandle } from 'types/hocTypes'
import React, { useMemo, useState, useEffect } from 'react'
import { ICv, IEmployer, IPersonRole, IProject } from 'types/cvsInterfaces'
import withProfileCard from 'Components/reusable/HOC/withProfileCard'
import { getEmployerInitialData } from 'Components/reusable/DataContext/InitialData'
import { employerSchema } from 'Components/reusable/DataContext/ValidationSchema'
import EmployerItemCard from './EmployerItemCard'
import EmployerModal from './EmployerModal'
import { useTranslation } from 'react-i18next'
import { IError } from 'types/error'

/** @notExported   */
interface IEmployerCardProps {
  /** Is card editable. */
  editable: boolean
  /** CV data. */
  cv: ICv
  /** Function for updating CV updated at. */
  setUpdatedAt: () => void
  /** Close card function. */
  closeItem: () => void
  /** Replace or add CV data function. */
  replaceOrAddItem: (newItem: IEmployer | { deleted: number }) => IEmployer[]
  /** Open CV item function. */
  openItem: (item: IEmployer) => void
  /** Edited CV item. */
  editedItem: IEmployer | null | undefined
  /** Employer history items. */
  items: IEmployer[]
  /** Header text. */
  header: string
  /** Is card expanded. */
  expanded: boolean
  /** Set CV roles function. */
  setRoles: (items: IPersonRole[]) => void
  /** Update CV overview data function. */
  updateOverviewData: (type: string, data: unknown | unknown[]) => void
  /** Update CV projects function. */
  updateProjects: (newProject: IProject) => void
  /** Is item anonymous. */
  anonymous?: boolean
  /** Update CV progress function. */
  updateProgress: () => void
  /** Dialog drafts from the CV wizard (see `withProfileCard`'s `itemDraft`). */
  itemDraft?: IItemDraftHandle
  /** Function to update projects list */
  setEmployers: (employers: IEmployer[]) => void
}

/**
 * Employer history card.
 *
 * @returns Employer history card component.
 * @notExported
 */
const EmployerHistoryCard: React.FC<IEmployerCardProps> = ({
  cv,
  editable,
  items,
  setUpdatedAt,
  closeItem,
  replaceOrAddItem,
  openItem,
  editedItem,
  header,
  expanded,
  setRoles,
  updateOverviewData,
  updateProjects,
  anonymous = false,
  updateProgress,
  itemDraft,
  setEmployers,
}) => {
  const { t } = useTranslation()
  const [updateConnected, setUpdateConnected] = useState<number>()

  useEffect(() => {
    const controller = new AbortController()

    if (updateConnected) {
      ;(async () => {
        const results = await cvRolesAPI.getList(updateConnected, controller)
        {
          setRoles(results)
          setUpdateConnected(undefined)
        }
      })()
    }

    return () => {
      controller.abort()
    }
  }, [setRoles, updateConnected])

  const convertToProject = async (item: IEmployer) => {
    try {
      const result = await cvAPI.convertToProject(item.id)
      {
        setUpdatedAt()
        updateOverviewData('employers', replaceOrAddItem({ deleted: item.id }))
        updateProgress()
        setUpdateConnected(item.cvId)
        updateProjects(result)
        const newEmployers = await cvEmployersAPI.getList(item.cvId)
        setEmployers(newEmployers)
        closeItem()
      }
    } catch (error) {
      const err = error as IError
      if (err.name !== 'CanceledError') {
        console.error(err)
      }
    }
  }

  const duplicateEmployer = async (item: IEmployer) => {
    const result = await cvEmployersAPI.create(cv.id, {
      ...item,
      id: undefined,
      translations: item.translations.map(tr => ({ ...tr, id: undefined })),
    })
    {
      setUpdatedAt()
      updateOverviewData('employers', replaceOrAddItem(result))
      updateProgress()
      setUpdateConnected(result.cvId)
      closeItem()
    }
  }

  const { i18n } = useTranslation()

  const itemCards = useMemo(
    () =>
      items.map((item, i) => (
        <EmployerItemCard
          expanded={expanded}
          key={`${item.id}-${i18n.language}`}
          profileWarningId={`profile-item-employers-${item.id}`}
          editable={editable}
          employer={item}
          onOpen={() => openItem(item)}
          header={header}
          anonymous={anonymous}
          showDivider={i < items.length - 1}
        />
      )),
    [anonymous, editable, expanded, header, items, openItem, i18n.language]
  )

  return (
    <>
      {itemCards}
      {editedItem !== undefined && (
        <EmployerModal
          key={editedItem?.id ?? 'new'}
          item={editedItem}
          draft={itemDraft?.forItem(editedItem ?? null)}
          cvId={cv.id}
          initialData={getEmployerInitialData(cv.id)}
          schema={employerSchema()}
          api={cvEmployersAPI}
          localeBase="profile.employer.modal"
          maxWidth="lg"
          fullWidth
          onClose={(value?: Record<string, unknown> | { id?: number; name?: string } | { newItem?: unknown }) => {
            const newItem = (value as { newItem?: unknown } | undefined)?.newItem as
              | IEmployer
              | { deleted: number; item?: IEmployer; cvId?: number }
              | undefined
            if (newItem && (('deleted' in newItem && newItem.deleted) || ('id' in newItem && newItem.id))) {
              const items = replaceOrAddItem(newItem)
              setUpdatedAt()
              updateOverviewData('employers', items)
              setUpdateConnected(newItem.cvId)
              setEmployers(items)
              updateProgress()
            }
            closeItem()
          }}
          extraButtonAction={(item: { id?: number; name?: string }) => convertToProject(item as IEmployer)}
          extraButtonLabel={t('profile.employer.model.convertToProject')}
          header={header}
          noEscClose
          duplicateFunction={(item: { id?: number; name?: string }) => duplicateEmployer(item as IEmployer)}
        />
      )}
    </>
  )
}

export default withProfileCard(EmployerHistoryCard as unknown as Parameters<typeof withProfileCard>[0])
